import React, { createContext } from 'react';
import { ListState } from 'react-stately';
import { Selection } from '@react-types/shared';

import { containsIconLeft, getDisabledKeys } from '../ListBoxBase/utils';
import { useObjectRef } from '../../hooks/useObjectRef';
import { getDefaultRenderer } from '../ListBoxBase/Render/Render';
import { getScrollbarClassName } from '../../lib/css/getScrollbarClassName';
import { getItemKey, getOptionKey } from '../ListBoxBase/Item/utils';

import { ListBoxItem, ListBoxOptionItem, ListBoxPropsRaw, ListBoxSectionItem } from './types';

export const ListBoxContext = createContext<ListState<ListBoxItem> | null>(null);

const getRenderItems = <ValueType extends string>() =>
    getDefaultRenderer<ListBoxOptionItem<ValueType>, ListBoxSectionItem<ValueType>>(getItemKey);

export function useListBoxContent<
    ValueType extends string,
    SectionType extends string,
    T extends ListBoxItem<ValueType, SectionType>,
>(props: ListBoxPropsRaw<ValueType, SectionType, T>, forwardedRef: React.ForwardedRef<HTMLDivElement>) {
    const {
        items,
        value,
        onChange,
        selectionMode = 'single',
        className,
        addonBefore,
        addonAfter,
        size,
        width,
        style,
        state,
        displaySelection,
        ...restProps
    } = props;
    const listProps = {
        items,
        children: getRenderItems(),
        selectionMode,
        selectedKeys: value,
        disabledKeys: getDisabledKeys<
            ValueType,
            SectionType,
            ListBoxOptionItem<ValueType>,
            ListBoxSectionItem<SectionType, ValueType>
        >(items, getOptionKey),
        'aria-label': props['aria-label'],
        onSelectionChange: (keys: Selection) => {
            // Исправить при редактировании. Автоматический игнор, см. DIRECT-193196.
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- см. выше
            onChange?.(Array.from(keys).map(key => key.toString()) as ValueType[]);
        },
        ...restProps,
        shouldSelectOnPressUp: false,
    };
    const ref = useObjectRef(forwardedRef);
    const scrollClass = getScrollbarClassName();
    const hasIconLeft = containsIconLeft<
        ValueType,
        SectionType,
        ListBoxOptionItem<ValueType>,
        ListBoxSectionItem<SectionType, ValueType>
    >(items);

    return {
        ref,
        hasIconLeft,
        scrollClass,
        listProps,
    };
}

export function processItems<ValueType extends string, SectionType extends string>({
    items,
    shouldAddAddon,
}: {
    items: ListBoxItem<ValueType, SectionType>[];
    shouldAddAddon: boolean;
}): ListBoxItem<ValueType, SectionType>[] {
    return shouldAddAddon
        ? [
              {
                  // в ValueType и SectionType могут прийти не строки,
                  // но элемент addon никогда не будет выбран, поэтому его тип не важен
                  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- автоматический игнор в рамках DIRECT-224975
                  key: 'addonSection' as SectionType,
                  options: [
                      {
                          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- автоматический игнор в рамках DIRECT-224975
                          value: 'addon' as ValueType,
                          content: 'addon',
                          disabled: true,
                      },
                  ],
              },
              ...items,
          ]
        : items;
}
